import { SettingsSection } from "@/components/molecules/settings-section";
import { Button } from "@/components/ui/button";
import { useUser } from "@/features/auth/hooks/use-user";
import { useTranslation } from "@/features/utils/i18n";
import { ArrowSquareOutIcon, CopyIcon } from "@phosphor-icons/react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PageLayout } from "../layout";

/**
 * The public API, for the tenant's own systems.
 *
 * A reference page rather than a configuration one: there is nothing to switch on here,
 * because the credentials already exist (the account's API keys) and the one thing that
 * *is* configurable — which countries a line may call — belongs on the channel it
 * applies to, next to the number it goes out from. What this page owes the reader is the
 * two things they cannot guess: the exact shape of the request, and the fact that a call
 * to another country is refused unless that channel says otherwise.
 */
const Snippet = ({ code }: { code: string }) => {
  const { t } = useTranslation("api");
  return (
    <div className="relative">
      <pre className="overflow-x-auto rounded-md border bg-muted/50 p-4 text-xs leading-relaxed">
        {code}
      </pre>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="absolute right-2 top-2 h-7 gap-1 text-xs"
        onClick={() => {
          void navigator.clipboard.writeText(code);
          toast.success(t("api.copied", "Copié"));
        }}
      >
        <CopyIcon className="h-3.5 w-3.5" />
        {t("api.copy", "Copier")}
      </Button>
    </div>
  );
};

/**
 * Stacked rather than side by side: a snippet is as wide as the command it
 * shows, and half of a narrow page would only give it a scrollbar.
 */
const Section = (props: React.ComponentProps<typeof SettingsSection>) => (
  <SettingsSection stacked {...props} />
);

export function ApiPage() {
  const { t } = useTranslation("api");
  const { company: clientId } = useParams<{ company: string }>();
  const { data: user } = useUser();

  const base = `${window.location.origin.replace("app.", "api.")}/api/public/v1/${clientId || "<client_id>"}`;
  const userId = user?.id || "<user_id>";

  return (
    <PageLayout
      width="medium"
      title={t("api.title")}
      description={t("api.description")}
    >
      <div>
        <Section
          title={t("api.auth.title")}
          description={t("api.auth.description")}
        >
          <Snippet code={`Authorization: Basic base64(${userId}:<api_key_secret>)`} />
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="outline" size="sm">
              <Link to="/account">
                {t("api.auth.manageKeys")}
                <ArrowSquareOutIcon className="ml-1 h-3.5 w-3.5" />
              </Link>
            </Button>
            <p className="text-xs text-muted-foreground">
              {t("api.auth.permission")}
            </p>
          </div>
        </Section>

        <Section
          title={t("api.calls.title")}
          description={t("api.calls.description")}
        >
          <Snippet
            code={`curl -X POST ${base}/calls \\
  -u "${userId}:<api_key_secret>" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "+33612345678",
    "purpose": "Rappeler au sujet du rendez-vous de jeudi, confirmer la présence.",
    "customer": { "given_name": "Marie", "family_name": "Dupont" }
  }'`}
          />
          <dl className="space-y-2 text-sm">
            <div>
              <dt className="font-medium">phone_number</dt>
              <dd className="text-muted-foreground">
                {t("api.calls.fields.phoneNumber")}
              </dd>
            </div>
            <div>
              <dt className="font-medium">purpose</dt>
              <dd className="text-muted-foreground">
                {t("api.calls.fields.purpose")}
              </dd>
            </div>
            <div>
              <dt className="font-medium">
                agent_id · language · timezone · customer
              </dt>
              <dd className="text-muted-foreground">
                {t("api.calls.fields.optional")}
              </dd>
            </div>
          </dl>
          <Snippet
            code={`{ "session_id": "…", "public_id": "…", "status": "ringing" }`}
          />
          <p className="text-sm text-muted-foreground">
            {t("api.calls.ringing")}
          </p>
        </Section>

        <Section
          title={t("api.callback.title")}
          description={t("api.callback.description")}
        >
          <Snippet
            code={`curl -X POST ${window.location.origin.replace("app.", "api.")}/api/public/v1/callback \\
  -H "Content-Type: application/json" \\
  -d '{
    "connector": "<web_connector_id>",
    "phone_number": "+33612345678"
  }'`}
          />
          <p className="text-sm text-muted-foreground">
            {t("api.callback.noKey")}
          </p>
          <p className="text-sm text-muted-foreground">
            {t("api.callback.noOptions")}
          </p>
          <p className="text-sm text-muted-foreground">{t("api.callback.limits")}</p>
          <Button asChild variant="outline" size="sm">
            <Link to={`/${clientId}/settings/connectors`}>
              {t("api.callback.enable")}
              <ArrowSquareOutIcon className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </Section>

        <Section
          title={t("api.countries.title")}
          description={t("api.countries.description")}
        >
          <p className="text-sm text-muted-foreground">
            {t("api.countries.scope")}
          </p>
          <Button asChild variant="outline" size="sm">
            <Link to={`/${clientId}/settings/connectors`}>
              {t("api.countries.configure")}
              <ArrowSquareOutIcon className="ml-1 h-3.5 w-3.5" />
            </Link>
          </Button>
        </Section>
      </div>
    </PageLayout>
  );
}
